import Loading from '@/shared/components/Carregando';
import { ApiReturn } from '@/modules/api.service';
import { Modal, Button } from 'react-bootstrap';
import { formatBytes } from '@/shared/utils/numeros';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';

/**
 * Base interface for attachment DTOs
 * All attachment types must have at least id and filename
 */
export interface BaseAttachment {
  id: number | string;
  filename: string;
}

/**
 * Service functions required for attachment management
 */
export interface AttachmentService<T extends BaseAttachment> {
  /**
   * List all attachments for an entity
   */
  enumerate: (entityId: number | string) => Promise<ApiReturn<T>>;

  /**
   * Create/upload a new attachment
   */
  create: (entityId: number | string, formData: FormData) => Promise<any>;

  /**
   * Download/view an attachment
   */
  download: (attachmentId: number | string, filename?: string) => Promise<any>;

  /**
   * Delete an attachment
   */
  delete: (attachmentId: number | string) => Promise<any>;
}

/**
 * Configuration for customizing the modal behavior and appearance
 */
export interface AttachmentModalConfig {
  /**
   * Modal title (default: "Enviar Arquivo")
   */
  title?: string;

  /**
   * Accepted file types (default: ".jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx,.pdf")
   */
  acceptFileTypes?: string;

  /**
   * Success message after upload (default: "Documento anexado")
   */
  uploadSuccessMessage?: string;

  /**
   * Warning message after delete (default: "Anexo removido")
   */
  deleteWarningMessage?: string;

  /**
   * Upload button text (default: "Enviar Anexo")
   */
  uploadButtonText?: string;

  /**
   * Loading text during upload (default: "Aguarde...")
   */
  uploadingText?: string;

  /**
   * Remove button text (default: "Remover")
   */
  removeButtonText?: string;

  /**
   * Table header for document column (default: "Documento")
   */
  documentColumnHeader?: string;

  /**
   * Table header for actions column (default: "Ações")
   */
  actionsColumnHeader?: string;
}

/**
 * Props for the generic AttachmentModal component
 */
export interface AttachmentModalProps<T extends BaseAttachment> {
  /**
   * ID of the entity to attach files to
   */
  entityId: number | string;

  /**
   * Whether the modal is visible
   */
  show: boolean;

  /**
   * Handler to close the modal
   */
  handleHide: (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement> | HTMLElement) => void;

  /**
   * Optional callback when files count changes (for updating parent component)
   */
  handleUpdateFilesCount?: () => void;

  /**
   * Service functions for CRUD operations
   */
  service: AttachmentService<T>;

  /**
   * Function to build the JSON data payload for upload
   * Receives filename and entityId, returns an object that will be stringified
   */
  buildUploadData: (filename: string, entityId: number | string) => Record<string, any>;

  /**
   * Optional configuration for customization
   */
  config?: AttachmentModalConfig;
}

/**
 * Generic modal component for uploading, viewing, and deleting attachments
 * Can be used with any entity type by providing appropriate service functions
 */
export default function AttachmentModal<T extends BaseAttachment>({ entityId, show, handleHide, handleUpdateFilesCount, service, buildUploadData, config = {} }: AttachmentModalProps<T>) {

  const {
    title = 'Enviar Arquivo',
    acceptFileTypes = '.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx,.pdf',
    uploadSuccessMessage = 'Documento anexado',
    deleteWarningMessage = 'Anexo removido',
    uploadButtonText = 'Enviar Anexo',
    uploadingText = 'Aguarde...',
    removeButtonText = 'Remover',
    documentColumnHeader = 'Documento',
    actionsColumnHeader = 'Ações',
  } = config;

  const [file, setFile] = useState<File>();
  const [loaded, setLoaded] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [registros, setRegistros] = useState<ApiReturn<T>>({ objects: [], count: 0 });

  const OnChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (show) {
      setLoaded(false);
    }
  }, [show]);

  useEffect(() => {
    if (!loaded && entityId) {
      service
        .enumerate(entityId)
        .then(async (result) => {
          setRegistros(result);
          handleUpdateFilesCount?.();
          setLoaded(true);
        })
        .catch((res) => {
          toast.error((res as string) + '');
        });
    }
  }, [entityId, loaded, service, handleUpdateFilesCount]);

  async function handleDownloadFile(id: string | number, filename?: string) {
    try {
      await service.download(id, filename);
    } catch (res) {
      toast.error((res as string) + '');
    }
  }

  async function handleDeleteFile(id: string | number) {
    await service
      .delete(id)
      .then(() => {
        setLoaded(false);
        toast.warning(deleteWarningMessage);
        handleUpdateFilesCount?.();
      })
      .catch((res) => {
        toast.error((res as string) + '');
      });
  }

  const handleUploadFile = async (e: React.MouseEvent<HTMLButtonElement>, arquivo: File) => {
    e.preventDefault();

    if (!arquivo) {
      toast.warn('Nenhum arquivo selecionado');
      return;
    }

    setEnviando(true);

    const dados = JSON.stringify(buildUploadData(arquivo.name, entityId));
    const formData = new FormData();
    formData.append('file', arquivo);
    formData.append('data', dados);

    await service
      .create(entityId, formData)
      .then(async () => {
        toast.success(uploadSuccessMessage);
      })
      .catch((err) => {
        toast.warn(`Erro: ${err}`);
      });
    setEnviando(false);
    setLoaded(false);
  };

  return (
    <Modal show={show} aria-labelledby="contained-modal-title-vcenter" centered onExit={handleHide} size="xl">
      <Modal.Header closeButton={false}>
        <Modal.Title id="contained-modal-title-vcenter">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="input-group">
          <input
            title="anexo"
            type="file"
            className="form-control"
            onChange={OnChange}
            accept={acceptFileTypes}
            disabled={enviando}
          />
          <span className="btn" title={file && file.type}>
            {file && file.type.substring(0, 15)}
          </span>
          <button type="button" className="btn">
            {file && formatBytes(file.size)}
          </button>
          <button
            type="button"
            className="btn btn-outline-success"
            disabled={enviando}
            title="Enviar Arquivo"
            onClick={(e) => {
              handleUploadFile(e, file!);
            }}
          >
            {!enviando ? uploadButtonText : uploadingText}
          </button>
        </div>
        <section>
          <br />
        </section>
        <table className="table table-sm table-bordered table-hover table-striped">
          <thead>
            <tr>
              <th>{documentColumnHeader}</th>
              <th className="text-center">{actionsColumnHeader}</th>
            </tr>
          </thead>
          <tbody>
            {registros && registros.objects && loaded ? (
              <>
                {registros.objects.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        <a
                          className="link"
                          onClick={() => {
                            handleDownloadFile(item.id, item.filename);
                          }}
                          title="Visualizar Documento"
                        >
                          {item.filename}
                        </a>
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDeleteFile(item.id)}
                        >
                          {removeButtonText}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </>
            ) : (
              <>
                <tr>
                  <td colSpan={3}>
                    <Loading />
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </Modal.Body>
      <Modal.Footer className="text-left">
        <Button variant="secondary" onClick={handleHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

